import { apiRequest, apiRequestPaginated } from './client';
import { mapJobStatus, mapPaymentStatus, nameFrom } from './mappers';
import type { ServiceRequest } from '../types';

type ApiPerson = string | { _id?: string; name?: string | null; email?: string; profilePicture?: string } | null;

type ApiJob = {
  _id: string;
  title?: string;
  user?: ApiPerson;
  provider?: ApiPerson;
  service?: string | { name?: string };
  category?: string | { name?: string };
  subcategory?: string | { name?: string };
  address?: string;
  date?: string;
  budget?: number;
  status?: string;
  paymentStatus?: string;
  createdAt?: string;
  description?: string;
};

const idFrom = (value?: ApiPerson): string => {
  if (!value || typeof value === 'string') return '';
  return value._id || '';
};

const categoryName = (value?: string | { name?: string }) => {
  if (!value) return '—';
  if (typeof value === 'string') return value;
  return value.name || '—';
};

export const mapApiJob = (job: ApiJob): ServiceRequest => ({
  id: job._id,
  customerId: idFrom(job.user),
  customerName: nameFrom(job.user),
  category: categoryName(job.category),
  providerId: idFrom(job.provider) || undefined,
  providerName: nameFrom(job.provider) === '—' ? undefined : nameFrom(job.provider),
  description: job.description || job.title || '',
  scheduledDate: job.date || '',
  scheduledTime: '',
  estimatedPrice: Number(job.budget) || 0,
  address: job.address || '—',
  city: '—',
  status: mapJobStatus(job.status),
  paymentStatus: mapPaymentStatus(job.paymentStatus),
  createdAt: typeof job.createdAt === 'string' ? job.createdAt : job.createdAt
    ? new Date(job.createdAt).toISOString()
    : '',
  timeline: [],
});

export const jobsApi = {
  getJobs: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    date?: string;
  }): Promise<{ jobs: ServiceRequest[]; total: number }> => {
    const query = new URLSearchParams({
      page: String(params?.page || 1),
      limit: String(params?.limit || 1000),
    });
    if (params?.search?.trim()) query.set('search', params.search.trim());
    if (params?.status) query.set('status', params.status);
    if (params?.date) query.set('date', params.date);

    const { data, pagination } = await apiRequestPaginated<{ jobs: ApiJob[] }>(
      `/admin/job/get-jobs?${query.toString()}`,
      { method: 'GET', auth: true }
    );

    return {
      jobs: (data.jobs || []).map(mapApiJob),
      total: pagination.totalItems || 0,
    };
  },

  getJobDetails: async (jobID: string): Promise<ServiceRequest> => {
    const data = await apiRequest<{ job: ApiJob }>(`/admin/job/get-job-details/${jobID}`, {
      method: 'GET',
      auth: true,
    });
    if (!data.job) throw new Error('Job not found');
    return mapApiJob(data.job);
  },
};
